import React from 'react'
import ContentfulQuery from '#src/containers/Contentful'
import cookies from 'js-cookie'
import config from '#src/config'
import { Experiment } from '@deal/experiment-js'
import { Button, Modal } from '@deal/components'
import { EngagementChannelCategoryFragment } from '#src/generated/graphql'
import { ContentfulResponse } from '../'
import { ExperimentPlacements } from '../../EngagementChannelHandler'
import styles from './styles.css'

interface V1Props {
  placement: ExperimentPlacements
  category: EngagementChannelCategoryFragment
  onSetCookie: () => void
  onDismissModal: () => void
  onSubmitModal: (categorySlug: string | null) => void
}

const V1: React.SFC<V1Props> = ({
  category,
  onDismissModal,
  onSubmitModal,
  onSetCookie,
  placement
}) => {
  const categorySlug = category.slug
  const fullPageTakeoverImage = category.contentAssets.fullPageTakeover
    ? category.contentAssets.fullPageTakeover.imageUrl
    : null

  // Used to separate the two placements and use one contentful type, e.g. `winter-sports-search`
  const categorySlugAndPlacement = `${categorySlug}-${placement}`

  return (
    <Experiment id={`fpt-${categorySlugAndPlacement}`} default="off">
      {({ result, loading }) => {
        if (loading) {
          return null
        }

        /**
         * This defaults to category slug and placement or uses the experiment result
         * if the exxperiment isn't killed or off. This requires the result be set up
         * in Contentful with a matching variation.
         */
        let contentfulVariation = categorySlugAndPlacement
        if (result && result !== 'control' && result !== 'off') {
          contentfulVariation = result
        }

        // Override the cookie set at the engagement channel handler level to include the variation specficity
        // Example cookie value: full-page-takeover-winter-sports-product-v1
        cookies.set('curated_landing_page_key', `full-page-takeover-${contentfulVariation}`, {
          domain: config.get('consumer.clientSideCookieDomain')
        })

        return (
          <ContentfulQuery template="fullPageTakeover" variation={contentfulVariation}>
            {({ content, loading }: ContentfulResponse) => {
              if (content) {
                return (
                  <Modal isOpen className={styles.takeover}>
                    <div className={styles.gradientMask} />
                    <div
                      className={styles.background}
                      style={{
                        backgroundImage: fullPageTakeoverImage
                          ? `url(${fullPageTakeoverImage}?auto=compress%2Cformat)`
                          : ''
                      }}
                    />
                    <div className={styles.content}>
                      <div className={styles.header}>{content.header}</div>
                      <Experiment id="full-page-takeover-button" default="v1">
                        {({ result }) =>
                          result === 'v1' && (
                            <div className={styles.action}>
                              <Button size="large" onClick={() => onSubmitModal(categorySlug)}>
                                {content.buttonText ? content.buttonText : 'Get started'}
                              </Button>
                            </div>
                          )
                        }
                      </Experiment>
                      <div className={styles.steps}>
                        {content.questions.map((question, idx) => (
                          <div className={styles.step} key={idx}>
                            <div className={styles.stepHeader}>{question.title}</div>
                            <div className={styles.stepBody}>{question.subtext}</div>
                          </div>
                        ))}
                      </div>
                      <Experiment id="full-page-takeover-button" default="v1">
                        {({ result }) =>
                          result === 'v2' && (
                            <div className={styles.action}>
                              <Button size="large" onClick={() => onSubmitModal(categorySlug)}>
                                {content.buttonText ? content.buttonText : 'Get started'}
                              </Button>
                            </div>
                          )
                        }
                      </Experiment>

                      <div className={styles.dismiss}>
                        <Button variant="link" onClick={onDismissModal}>
                          {content.dismissButtonText ? content.dismissButtonText : 'Maybe later'}
                        </Button>
                      </div>
                    </div>
                  </Modal>
                )
              } else if (!loading && !content) {
                /**
                 * In the event there isn't content set up for the full page takeover for this
                 * category (and we're on the search page), we want to set the cookie. We don't
                 * want the module to popup if they switch to a category that has a contentful
                 * entry.
                 */
                if (placement === 'search') {
                  onSetCookie()
                }
              }

              return null
            }}
          </ContentfulQuery>
        )
      }}
    </Experiment>
  )
}

export default V1
